<template>
  <transition name="toast">
    <div v-if="visible" class="toast" :class="toastClass">
      <v-icon :size="20" :color="iconColor">{{ icon }}</v-icon>
      <span class="toast-message">{{ message }}</span>
      <button v-if="showClose" @click="close" class="toast-close">
        <v-icon size="16">mdi-close</v-icon>
      </button>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'ToastNotification',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'success', // success, error, info, warning
      validator: (value) => ['success', 'error', 'info', 'warning'].includes(value)
    },
    message: {
      type: String,
      required: true
    },
    duration: {
      type: Number,
      default: 3000
    },
    showClose: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      timeout: null
    }
  },
  computed: {
    toastClass() {
      return `toast-${this.type}`;
    },
    icon() {
      switch (this.type) {
        case 'success':
          return 'mdi-check-circle';
        case 'error':
          return 'mdi-alert-circle';
        case 'warning':
          return 'mdi-alert';
        case 'info':
          return 'mdi-information';
        default:
          return 'mdi-information';
      }
    },
    iconColor() {
      switch (this.type) {
        case 'success':
          return '#4CAF50';
        case 'error':
          return '#F44336';
        case 'warning':
          return '#FF9800';
        case 'info':
          return '#2196F3';
        default:
          return '#9E9E9E';
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.duration > 0) {
        this.setTimeout();
      } else {
        this.clearTimeout();
      }
    }
  },
  methods: {
    setTimeout() {
      this.clearTimeout();
      this.timeout = setTimeout(() => {
        this.close();
      }, this.duration);
    },
    clearTimeout() {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }
    },
    close() {
      this.clearTimeout();
      this.$emit('close');
    }
  },
  beforeDestroy() {
    this.clearTimeout();
  }
}
</script>

<style scoped>
.toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  left: 24px;
  max-width: 400px;
  margin: 0 auto;
  background: #FFFFFF;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 1000;
  direction: rtl;
  animation: toastSlideIn 0.3s ease-out;
}

@keyframes toastSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.toast-success {
  border-right: 4px solid #4CAF50;
}

.toast-error {
  border-right: 4px solid #F44336;
}

.toast-warning {
  border-right: 4px solid #FF9800;
}

.toast-info {
  border-right: 4px solid #2196F3;
}

.toast-message {
  flex: 1;
  font-size: 14px;
  color: #424242;
  font-weight: 500;
}

.toast-close {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9E9E9E;
  transition: all 0.2s ease;
}

.toast-close:hover {
  background: #F5F5F5;
  color: #424242;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter,
.toast-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

@media (max-width: 600px) {
  .toast {
    right: 16px;
    left: 16px;
    bottom: 16px;
  }
}
</style>


